import React, {
    useEffect,
    useState
} from 'react';
import {
    Link,
    useNavigate,
    useParams
} from 'react-router-dom';
import './adminPortfolio.css';
import getImageUrl from '../../../utils/getImageUrl';
import API_BASE_URL from '../../../config/api';

const categories = [
    'LED 전광판',
    '빔프로젝터',
    '설치',
    '기업행사',
    '전시 · 박람회',
    '종교시설'
];

const AdminPortfolioForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const isEditMode = Boolean(id);

    const [formData, setFormData] = useState({
        title: '',
        category: '',
        industry: '',
        product: '',
        contractor: '',
        location: '',
        workDate: '',
        description: '',
        sortOrder: 0,
        isActive: true
    });

    const [thumbnail, setThumbnail] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState('');
    const [existingThumbnail, setExistingThumbnail] = useState('');

    const [detailImages, setDetailImages] = useState([]);
    const [detailPreviews, setDetailPreviews] = useState([]);
    const [existingDetailImages, setExistingDetailImages] = useState([]);

    const [isLoading, setIsLoading] = useState(isEditMode);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isEditMode) {
            return;
        }

        const fetchPortfolio = async () => {
            try {
                setIsLoading(true);

                const response = await fetch(
                    `${API_BASE_URL}/api/portfolios/${id}`,
                    {
                        method: 'GET',
                        credentials: 'include'
                    }
                );

                const result = await response.json();

                if (!response.ok) {
                    throw new Error(
                        result.message ||
                        '시공사례를 불러오지 못했습니다.'
                    );
                }

                const portfolio = result.portfolio;

                setFormData({
                    title: portfolio.title || '',
                    category: portfolio.category || '',
                    industry: portfolio.industry || '',
                    product: portfolio.product || '',
                    contractor: portfolio.contractor || '',
                    location: portfolio.location || '',
                    workDate: portfolio.work_date
                        ? portfolio.work_date.substring(0, 10)
                        : '',
                    description: portfolio.description || '',
                    sortOrder: portfolio.sort_order ?? 0,
                    isActive: portfolio.is_active
                });

                setExistingThumbnail(
                    portfolio.thumbnail_image || ''
                );

                setExistingDetailImages(
                    portfolio.detail_images || []
                );
            } catch (error) {
                console.error(
                    '시공사례 상세 조회 오류:',
                    error
                );

                alert(
                    error.message
                );

                navigate(
                    '/admin/portfolios'
                );
            } finally {
                setIsLoading(false);
            }
        };

        fetchPortfolio();
    }, [
        id,
        isEditMode,
        navigate
    ]);

    useEffect(() => {
        return () => {
            if (thumbnailPreview) {
                URL.revokeObjectURL(
                    thumbnailPreview
                );
            }
        };
    }, [thumbnailPreview]);

    useEffect(() => {
        return () => {
            detailPreviews.forEach((preview) => {
                URL.revokeObjectURL(
                    preview
                );
            });
        };
    }, [detailPreviews]);

    const handleChange = (event) => {
        const {
            name,
            value,
            type,
            checked
        } = event.target;

        setFormData((prev) => ({
            ...prev,
            [name]:
                type === 'checkbox'
                    ? checked
                    : value
        }));
    };

    const handleThumbnailChange = (event) => {
        const file =
            event.target.files?.[0];

        if (!file) {
            return;
        }

        if (thumbnailPreview) {
            URL.revokeObjectURL(
                thumbnailPreview
            );
        }

        const preview =
            URL.createObjectURL(file);

        setThumbnail(file);
        setThumbnailPreview(preview);
    };

    const handleDetailImagesChange = (event) => {
        const files = Array.from(
            event.target.files || []
        );

        if (files.length === 0) {
            return;
        }

        if (files.length > 10) {
            alert(
                '상세 이미지는 최대 10장까지 등록할 수 있습니다.'
            );

            event.target.value = '';

            return;
        }

        detailPreviews.forEach((preview) => {
            URL.revokeObjectURL(
                preview
            );
        });

        const previews = files.map(
            (file) => URL.createObjectURL(file)
        );

        setDetailImages(files);
        setDetailPreviews(previews);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!formData.title.trim()) {
            alert('제목을 입력해주세요.');
            return;
        }

        if (!formData.category) {
            alert('카테고리를 선택해주세요.');
            return;
        }

        if (!formData.industry.trim()) {
            alert('업종을 입력해주세요.');
            return;
        }

        if (!formData.product.trim()) {
            alert('설치제품을 입력해주세요.');
            return;
        }

        if (!formData.contractor.trim()) {
            alert('시공사를 입력해주세요.');
            return;
        }

        if (!isEditMode && !thumbnail) {
            alert('대표 이미지를 등록해주세요.');
            return;
        }

        try {
            setIsSubmitting(true);

            const data =
                new FormData();

            data.append(
                'title',
                formData.title
            );

            data.append(
                'category',
                formData.category
            );

            data.append(
                'industry',
                formData.industry
            );

            data.append(
                'product',
                formData.product
            );

            data.append(
                'contractor',
                formData.contractor
            );

            data.append(
                'location',
                formData.location
            );

            data.append(
                'workDate',
                formData.workDate
            );

            data.append(
                'description',
                formData.description
            );

            data.append(
                'sortOrder',
                formData.sortOrder
            );

            data.append(
                'isActive',
                String(formData.isActive)
            );

            if (thumbnail) {
                data.append(
                    'thumbnail',
                    thumbnail
                );
            }

            detailImages.forEach((file) => {
                data.append(
                    'detailImages',
                    file
                );
            });

            if (isEditMode) {
                data.append(
                    'replaceDetailImages',
                    detailImages.length > 0
                        ? 'true'
                        : 'false'
                );
            }

            const url = isEditMode
                ? `${API_BASE_URL}/api/portfolios/${id}`
                : `${API_BASE_URL}/api/portfolios`;

            const response = await fetch(
                url,
                {
                    method: isEditMode
                        ? 'PUT'
                        : 'POST',
                    credentials: 'include',
                    body: data
                }
            );

            const result =
                await response.json();

            if (!response.ok) {
                throw new Error(
                    result.message ||
                    (
                        isEditMode
                            ? '시공사례 수정에 실패했습니다.'
                            : '시공사례 등록에 실패했습니다.'
                    )
                );
            }

            alert(
                isEditMode
                    ? '시공사례가 수정되었습니다.'
                    : '시공사례가 등록되었습니다.'
            );

            navigate(
                '/admin/portfolios'
            );
        } catch (error) {
            console.error(
                isEditMode
                    ? '시공사례 수정 오류:'
                    : '시공사례 등록 오류:',
                error
            );

            alert(
                error.message
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="admin-portfolio">
                <div className="admin-portfolio-empty">
                    <i className="mdi mdi-loading mdi-spin"></i>

                    <strong>
                        시공사례를 불러오는 중입니다.
                    </strong>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-portfolio">
            <div className="admin-page-header">
                <div>
                    <p>
                        PORTFOLIO
                    </p>

                    <h1>
                        {isEditMode
                            ? '시공사례 수정'
                            : '시공사례 등록'
                        }
                    </h1>

                    <span>
                        {isEditMode
                            ? '등록된 시공사례의 정보를 수정합니다.'
                            : '홈페이지에 노출할 새로운 시공사례를 등록합니다.'
                        }
                    </span>
                </div>

                <Link
                    to="/admin/portfolios"
                    className="admin-portfolio-back"
                >
                    <i className="mdi mdi-arrow-left"></i>
                    목록으로
                </Link>
            </div>

            <form
                className="admin-portfolio-form"
                onSubmit={handleSubmit}
            >
                <section className="admin-form-section">
                    <div className="admin-form-section-title">
                        <h2>
                            기본 정보
                        </h2>

                        <p>
                            시공사례의 기본 정보를 입력해주세요.
                        </p>
                    </div>

                    <div className="admin-form-grid">
                        <div className="admin-form-group">
                            <label htmlFor="title">
                                제목
                                <em>*</em>
                            </label>

                            <input
                                id="title"
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="시공사례 제목을 입력해주세요."
                            />
                        </div>

                        <div className="admin-form-group">
                            <label htmlFor="category">
                                카테고리
                                <em>*</em>
                            </label>

                            <select
                                id="category"
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                            >
                                <option value="">
                                    카테고리를 선택해주세요.
                                </option>

                                {categories.map(
                                    (category) => (
                                        <option
                                            key={category}
                                            value={category}
                                        >
                                            {category}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>

                        <div className="admin-form-group">
                            <label htmlFor="industry">
                                업종
                                <em>*</em>
                            </label>

                            <input
                                id="industry"
                                type="text"
                                name="industry"
                                value={formData.industry}
                                onChange={handleChange}
                                placeholder="예: 기업행사, 전시회, 교회"
                            />
                        </div>

                        <div className="admin-form-group">
                            <label htmlFor="product">
                                설치제품
                                <em>*</em>
                            </label>

                            <input
                                id="product"
                                type="text"
                                name="product"
                                value={formData.product}
                                onChange={handleChange}
                                placeholder="예: P2.5 LED 전광판"
                            />
                        </div>

                        <div className="admin-form-group">
                            <label htmlFor="contractor">
                                시공사
                                <em>*</em>
                            </label>

                            <input
                                id="contractor"
                                type="text"
                                name="contractor"
                                value={formData.contractor}
                                onChange={handleChange}
                                placeholder="예: 엠티미디어"
                            />
                        </div>

                        <div className="admin-form-group">
                            <label htmlFor="location">
                                장소
                            </label>

                            <input
                                id="location"
                                type="text"
                                name="location"
                                value={formData.location}
                                onChange={handleChange}
                                placeholder="예: 서울 코엑스"
                            />
                        </div>

                        <div className="admin-form-group">
                            <label htmlFor="workDate">
                                시공일
                            </label>

                            <input
                                id="workDate"
                                type="date"
                                name="workDate"
                                value={formData.workDate}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="admin-form-group full">
                            <label htmlFor="description">
                                시공 내용
                            </label>

                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="시공 내용을 입력해주세요."
                            />
                        </div>
                    </div>
                </section>

                <section className="admin-form-section">
                    <div className="admin-form-section-title">
                        <h2>
                            대표 이미지
                            {!isEditMode && (
                                <em>*</em>
                            )}
                        </h2>

                        <p>
                            시공사례 목록에 표시되는 대표 이미지입니다.
                        </p>
                    </div>

                    <label className="admin-image-upload">
                        {thumbnailPreview ? (
                            <img
                                src={thumbnailPreview}
                                alt="새 대표 이미지 미리보기"
                            />
                        ) : existingThumbnail ? (
                            <img
                                src={
                                    getImageUrl(existingThumbnail)
                                }
                                alt="기존 대표 이미지"
                            />
                        ) : (
                            <div className="admin-image-upload-empty">
                                <i className="mdi mdi-image-plus-outline"></i>

                                <strong>
                                    대표 이미지 선택
                                </strong>

                                <span>
                                    JPG, PNG, WEBP / 최대 10MB
                                </span>
                            </div>
                        )}

                        <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={handleThumbnailChange}
                        />
                    </label>

                    {isEditMode && (
                        <div className="admin-image-edit-guide">
                            <i className="mdi mdi-information-outline"></i>

                            새 이미지를 선택하지 않으면 기존 대표 이미지가 유지됩니다.
                        </div>
                    )}
                </section>

                <section className="admin-form-section">
                    <div className="admin-form-section-title">
                        <h2>
                            상세 이미지
                        </h2>

                        <p>
                            상세페이지에 표시할 이미지를 최대 10장까지 등록할 수 있습니다.
                        </p>
                    </div>

                    {isEditMode &&
                        detailImages.length === 0 &&
                        existingDetailImages.length > 0 && (
                            <>
                                <div className="admin-existing-image-title">
                                    현재 등록된 이미지
                                </div>

                                <div className="admin-detail-preview">
                                    {existingDetailImages.map(
                                        (image, index) => (
                                            <div
                                                key={image.portfolio_image_id}
                                                className="admin-detail-preview-item"
                                            >
                                                <img
                                                    src={
                                                        getImageUrl(image.image_url)
                                                    }
                                                    alt={`기존 상세 이미지 ${index + 1}`}
                                                />

                                                <span>
                                                    {index + 1}
                                                </span>
                                            </div>
                                        )
                                    )}
                                </div>
                            </>
                        )}

                    <label className="admin-detail-upload">
                        <i className="mdi mdi-image-plus-outline"></i>

                        {isEditMode
                            ? '상세 이미지 새로 선택'
                            : '상세 이미지 선택'
                        }

                        <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            multiple
                            onChange={handleDetailImagesChange}
                        />
                    </label>

                    {detailPreviews.length > 0 && (
                        <>
                            {isEditMode && (
                                <div className="admin-image-replace-guide">
                                    새 이미지가 선택되었습니다. 수정 완료 시 기존 상세 이미지는 아래 이미지로 교체됩니다.
                                </div>
                            )}

                            <div className="admin-detail-preview">
                                {detailPreviews.map(
                                    (preview, index) => (
                                        <div
                                            key={preview}
                                            className="admin-detail-preview-item"
                                        >
                                            <img
                                                src={preview}
                                                alt={`상세 이미지 ${index + 1}`}
                                            />

                                            <span>
                                                {index + 1}
                                            </span>
                                        </div>
                                    )
                                )}
                            </div>
                        </>
                    )}
                </section>

                <section className="admin-form-section">
                    <div className="admin-form-section-title">
                        <h2>
                            노출 설정
                        </h2>

                        <p>
                            홈페이지 공개 여부와 정렬 순서를 설정합니다.
                        </p>
                    </div>

                    <div className="admin-form-grid">
                        <div className="admin-form-group">
                            <label htmlFor="sortOrder">
                                정렬 순서
                            </label>

                            <input
                                id="sortOrder"
                                type="number"
                                name="sortOrder"
                                min="0"
                                value={formData.sortOrder}
                                onChange={handleChange}
                            />

                            <small>
                                숫자가 작을수록 먼저 표시됩니다.
                            </small>
                        </div>

                        <div className="admin-form-group admin-form-switch-group">
                            <label>
                                공개 여부
                            </label>

                            <label className="admin-form-switch">
                                <input
                                    type="checkbox"
                                    name="isActive"
                                    checked={formData.isActive}
                                    onChange={handleChange}
                                />

                                <span></span>

                                <strong>
                                    {formData.isActive
                                        ? '공개'
                                        : '비공개'
                                    }
                                </strong>
                            </label>
                        </div>
                    </div>
                </section>

                <div className="admin-form-actions">
                    <Link
                        to="/admin/portfolios"
                        className="admin-form-cancel"
                    >
                        취소
                    </Link>

                    <button
                        type="submit"
                        className="admin-form-submit"
                        disabled={isSubmitting}
                    >
                        {isSubmitting
                            ? (
                                isEditMode
                                    ? '수정 중...'
                                    : '등록 중...'
                            )
                            : (
                                isEditMode
                                    ? '수정하기'
                                    : '등록하기'
                            )
                        }
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminPortfolioForm;